import { Controller } from "@hotwired/stimulus"
import { mapService } from "@/services/map_service"
import MapInitializationService from "@/services/map_initialization_service"
import PolygonService from "@/services/polygon_service"
import CoordinateValidationService from "@/services/coordinate_validation_service"
import { errorHandler } from "@/services/error_handler_service"
import type * as maplibregl from "maplibre-gl"
import type { Feature, Polygon } from "geojson"

interface PlantData {
  id: number
  x: number
  y: number
  name: string
  image_url: string | null
  width: number | null
}

interface MapPosition {
  lat: number
  lng: number
}

export default class extends Controller {
  static override values = {
    geoJson: String,
    gardenId: Number,
    plants: Array
  }

  declare readonly geoJsonValue: string
  declare readonly gardenIdValue: number
  declare readonly plantsValue: PlantData[]
  declare readonly hasGeoJsonValue: boolean
  declare readonly hasPlantsValue: boolean

  private map: maplibregl.Map | undefined = undefined
  private markers: Map<number, maplibregl.Marker> = new Map()
  private boundaryPath: Array<{lat: number; lng: number}> = []
  private resizeObserver: ResizeObserver | undefined = undefined

  override async connect() {
    try {
      await mapService.load()
      await this.initMap()
    } catch (error) {
      console.error('Failed to load map service:', error)
      this.showErrorMessage('Failed to load map. Please refresh the page.')
    }
  }

  async initMap() {
    if (!this.hasGeoJsonValue) return

    try {
      const path = MapInitializationService.geoJsonToPath(this.geoJsonValue)

      if (!path || path.length === 0) {
        console.warn('Invalid GeoJSON or no valid coordinates')
        return
      }

      const calculatedCenter = MapInitializationService.calculateCenterFromGeoJson(this.geoJsonValue)
      const center = calculatedCenter || path[0]

      if (!center) {
        console.error('Unable to determine map center')
        return
      }

      const mapTypeId = mapService.getMapTypeId()

      this.boundaryPath = path

      this.map = await mapService.createMap(this.mapElement, {
        center,
        zoom: 18,
        mapTypeId: mapTypeId.TERRAIN,
        mapTypeControl: true,
        onStyleChanged: () => this.redrawOverlays()
      }) as maplibregl.Map

      await PolygonService.fitMapToBounds(mapService, this.map, path)
      this.map.fitBounds(this.map.getBounds(), { padding: 40, animate: false })

      // Wait for the camera animation to finish before drawing overlays,
      // so the polygon layer and markers are placed on a settled viewport.
      await new Promise<void>(resolve => {
        if (!this.map!.isMoving()) {
          resolve()
        } else {
          this.map!.once('idle', () => resolve())
        }
      })

      await this.drawPropertyBoundary(path)
      await this.positionPlants()

      this.startResizeObserver()
      requestAnimationFrame(() => {
        this.map?.resize()
      })
    } catch (e) {
      console.error('Error initializing property boundary garden map:', e)
    }
  }

  /** MapLibre only tracks window resize by default; flex growth needs container observation. */
  private startResizeObserver() {
    if (!this.map) return

    const container = this.mapElement.parentElement
    if (!container || typeof ResizeObserver === 'undefined') return

    this.resizeObserver?.disconnect()
    this.resizeObserver = new ResizeObserver(() => {
      this.map?.resize()
    })
    this.resizeObserver.observe(container)
  }

  override disconnect() {
    this.resizeObserver?.disconnect()
    this.resizeObserver = undefined
    this.markers.forEach((marker) => marker.remove())
    this.markers.clear()
    this.map?.remove()
    this.map = undefined
  }

  private async redrawOverlays() {
    if (!this.map || this.boundaryPath.length === 0) return
    // Remove existing markers before re-adding them
    this.markers.forEach(marker => marker.remove())
    this.markers.clear()
    await this.drawPropertyBoundary(this.boundaryPath)
    await this.positionPlants()
  }

  async drawPropertyBoundary(path: Array<{lat: number; lng: number}>) {
    if (!this.map) return

    await mapService.createPolygon({
      paths: path,
      strokeColor: '#4CAF50',
      strokeOpacity: 0.8,
      strokeWeight: 3,
      fillColor: '#4CAF50',
      fillOpacity: 0.1,
      map: this.map
    })
  }

  async positionPlants() {
    if (!this.hasPlantsValue || this.plantsValue.length === 0) return

    // Compute meter-to-pixel scale at the current map zoom and center latitude,
    // so we can size each marker proportionally to the plant's real-world width.
    const zoom   = this.map!.getZoom()
    const bounds = this.map!.getBounds()
    const lat    = bounds.getCenter().lat
    const metersPerPixel = this.getMetersPerPixel(lat, zoom)

    for (const plant of this.plantsValue) {
      const mapPosition = await this.convertGridToMapCoordinates(plant.x, plant.y)
      if (mapPosition) {
        await this.createPlantMarker(plant, mapPosition, metersPerPixel)
      }
    }
  }

  // Helper method to validate and filter coordinate points
  private validateCoordinates(path: MapPosition[]): MapPosition[] {
    return path.filter(point => CoordinateValidationService.isValidCoordinate(point))
  }

  /** Returns approximate meters represented by one screen pixel at the given
   *  latitude and MapLibre zoom level. */
  private getMetersPerPixel(lat: number, zoom: number): number {
    return 40075016.686 * Math.cos(lat * Math.PI / 180) / Math.pow(2, zoom + 8)
  }

  async convertGridToMapCoordinates(gridX: number, gridY: number): Promise<MapPosition | null> {
    if (!this.map || !this.hasGeoJsonValue) return null

    try {
      const bounds = await this.getBoundaryBounds()
      if (!bounds) return null

      const { ne, sw } = bounds

      // Grid coords are 0-100; normalize to 0-1 for interpolation
      const normalizedX = gridX / 100
      const normalizedY = gridY / 100

      return {
        lat: sw.lat + (ne.lat - sw.lat) * normalizedY,
        lng: sw.lng + (ne.lng - sw.lng) * normalizedX
      }
    } catch (e) {
      console.error('Error converting grid coordinates:', e)
      return null
    }
  }

  /** Convert a map lat/lng back to 0-100 grid coordinates. */
  private async convertMapToGridCoordinates(lat: number, lng: number): Promise<{ x: number; y: number } | null> {
    try {
      const bounds = await this.getBoundaryBounds()
      if (!bounds) return null

      const { ne, sw } = bounds

      return {
        x: ((lng - sw.lng) / (ne.lng - sw.lng)) * 100,
        y: ((lat - sw.lat) / (ne.lat - sw.lat)) * 100
      }
    } catch (e) {
      console.error('Error converting map coordinates to grid:', e)
      return null
    }
  }

  /** Parse the GeoJSON boundary and return NE / SW corners of its bounding box. */
  private async getBoundaryBounds(): Promise<{ ne: MapPosition; sw: MapPosition } | null> {
    if (!this.hasGeoJsonValue) return null

    const geoJSON: Feature = JSON.parse(this.geoJsonValue)
    if (!geoJSON.geometry || geoJSON.geometry.type !== 'Polygon') {
      console.warn('Invalid GeoJSON structure')
      return null
    }

    const polygon     = geoJSON.geometry as Polygon
    const coordinates = polygon.coordinates[0]
    if (!coordinates || coordinates.length === 0) return null

    const path: MapPosition[] = coordinates.map((coord: number[]) => ({
      lat: coord[1] || 0,
      lng: coord[0] || 0
    }))

    const validPath = this.validateCoordinates(path)
    if (validPath.length === 0) return null

    const boundsObj = mapService.createBounds()
    validPath.forEach(p => boundsObj.extend(mapService.createLatLng(p.lat, p.lng)))

    const ne = boundsObj.getNorthEast?.()
    const sw = boundsObj.getSouthWest?.()
    if (!ne || !sw) return null

    return { ne, sw }
  }

  /** Ray-casting point-in-polygon check against the GeoJSON boundary. */
  private async isInsideBoundary(lat: number, lng: number): Promise<boolean> {
    if (!this.hasGeoJsonValue) return true

    try {
      const geoJSON: Feature = JSON.parse(this.geoJsonValue)
      if (!geoJSON.geometry || geoJSON.geometry.type !== 'Polygon') return true

      const polygon     = geoJSON.geometry as Polygon
      const coordinates = polygon.coordinates[0]
      if (!coordinates || coordinates.length === 0) return true

      let inside = false
      for (let i = 0, j = coordinates.length - 1; i < coordinates.length; j = i++) {
        const ci = coordinates[i]!, cj = coordinates[j]!
        const xi = ci[0]!, yi = ci[1]!  // lng, lat
        const xj = cj[0]!, yj = cj[1]!
        const intersect = (yi > lat) !== (yj > lat) &&
                          lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi
        if (intersect) inside = !inside
      }
      return inside
    } catch {
      return true
    }
  }

  async createPlantMarker(plant: PlantData, position: MapPosition, metersPerPixel: number) {
    if (!this.map) return

    const markerElement = this.createPlantMarkerElement(plant, metersPerPixel)

    const marker = await mapService.createMarker({
      position,
      map: this.map,
      content: markerElement,
      title: plant.name
    }) as maplibregl.Marker

    this.markers.set(plant.id, marker)
    this.attachDragHandlers(marker, plant)
  }

  /** Wire up MapLibre drag events (works for both mouse and touch). */
  private attachDragHandlers(marker: maplibregl.Marker, plant: PlantData) {
    marker.setDraggable(true)

    let originalLngLat: maplibregl.LngLat

    marker.on('dragstart', () => {
      originalLngLat = marker.getLngLat()
    })

    marker.on('dragend', async () => {
      const newLngLat = marker.getLngLat()

      // Boundary check
      const inside = await this.isInsideBoundary(newLngLat.lat, newLngLat.lng)
      if (!inside) {
        marker.setLngLat(originalLngLat)
        this.showErrorMessage('Plant must be placed within the garden boundary.')
        return
      }

      // Convert back to grid coords
      const grid = await this.convertMapToGridCoordinates(newLngLat.lat, newLngLat.lng)
      if (!grid) {
        marker.setLngLat(originalLngLat)
        return
      }

      const clampedX = Math.max(0, Math.min(100, grid.x))
      const clampedY = Math.max(0, Math.min(100, grid.y))

      // Optimistic: marker is already at new position. Save to backend.
      const ok = await this.savePlantPosition(plant.id, clampedX, clampedY)
      if (!ok) {
        marker.setLngLat(originalLngLat)
        this.showErrorMessage('Failed to save plant position. Please try again.')
      }
    })
  }

  createPlantMarkerElement(plant: PlantData, metersPerPixel: number): HTMLElement {
    // Compute pixel diameter from real-world width, clamped to a sensible range
    const widthM       = plant.width ?? 1
    const rawPixels    = widthM / metersPerPixel
    const pixelSize    = Math.max(20, Math.min(80, rawPixels))
    const circleSize   = pixelSize - 4  // inner circle is slightly smaller than the hit area

    const markerElement = document.createElement('div')
    markerElement.classList.add('property-boundary-garden-marker')
    markerElement.style.width  = `${pixelSize}px`
    markerElement.style.height = `${pixelSize}px`
    markerElement.style.cursor = 'grab'

    const circle = document.createElement('div')
    circle.style.width        = `${circleSize}px`
    circle.style.height       = `${circleSize}px`
    circle.style.borderRadius = '50%'
    circle.style.border       = '2px solid #4CAF50'
    circle.style.position     = 'absolute'
    circle.style.top          = '2px'
    circle.style.left         = '2px'
    circle.style.overflow     = 'hidden'

    if (plant.image_url) {
      circle.style.backgroundImage    = `url(${plant.image_url})`
      circle.style.backgroundSize     = 'cover'
      circle.style.backgroundPosition = 'center'
    } else {
      circle.style.backgroundColor = '#4CAF50'
      circle.style.display         = 'flex'
      circle.style.alignItems      = 'center'
      circle.style.justifyContent  = 'center'
      circle.style.fontSize        = `${Math.max(10, circleSize * 0.4)}px`
      circle.innerHTML = '🌱'
    }

    const label = document.createElement('div')
    label.textContent              = plant.name
    label.style.position           = 'absolute'
    label.style.bottom             = '100%'
    label.style.left               = '50%'
    label.style.transform          = 'translateX(-50%)'
    label.style.marginBottom       = '2px'
    label.style.fontSize           = '8px'
    label.style.color              = 'white'
    label.style.backgroundColor    = 'rgba(0,0,0,0.7)'
    label.style.padding            = '1px 3px'
    label.style.borderRadius       = '2px'
    label.style.whiteSpace         = 'nowrap'
    label.style.maxWidth           = '60px'
    label.style.overflow           = 'hidden'
    label.style.textOverflow       = 'ellipsis'
    label.style.pointerEvents      = 'none'

    markerElement.appendChild(circle)
    markerElement.appendChild(label)

    return markerElement
  }

  private async savePlantPosition(placementId: number, x: number, y: number): Promise<boolean> {
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
    try {
      const response = await fetch(
        `/gardens/${this.gardenIdValue}/plant_placements/${placementId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept':       'application/json',
            'X-CSRF-Token': csrfToken
          },
          body: JSON.stringify({ plant_placement: { x_coord: x, y_coord: y } })
        }
      )
      return response.ok
    } catch {
      return false
    }
  }

  get mapElement(): HTMLElement {
    return this.element.querySelector('[id^="garden-map-"]') as HTMLElement
  }

  showErrorMessage(message: string) {
    errorHandler.showError(message)
  }
}
